import './Style.scss';
import * as React from 'react';
import { Button, Form, ProgressBar, Table } from 'react-bootstrap';
import { getLocalization } from 'global/global';
import { useAppSelector } from 'index';
import { AccessLogItem } from 'Interfaces/Admin';
import { fetchAccessLogs } from 'api/adminApi';
import { adminGroupsSelector, adminUsersSelector } from 'reducers/admin/selectors';
import { formatDate } from '../AdminGrid/columns';

/** Replaces AccessLogView.js. */
export default function AccessLogs() {
  const users = useAppSelector(adminUsersSelector);
  const groups = useAppSelector(adminGroupsSelector);
  const ui = useAppSelector((state) => state.admin.ui);

  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [items, setItems] = React.useState<AccessLogItem[] | null>(null);
  const [loading, setLoading] = React.useState(false);

  const selection = ui.gridSelection;
  /** Logs are always reported for whatever is ticked in the grid. */
  const mode = ui.gridMode === 'groups' ? 'groups' : 'users';

  const subject = React.useMemo(() => {
    if (selection.length === 0) {
      return getLocalization('noUserGroup');
    }
    const names = mode === 'groups'
      ? selection.map((id) => groups.find((g) => g.id === id)?.name)
      : selection.map((id) => users.find((u) => u.id === id)?.userName);
    return `${getLocalization(mode === 'groups' ? 'group' : 'user')} : ${names.filter(Boolean).join(', ')}`;
  }, [selection, mode, users, groups]);

  const load = React.useCallback(() => {
    setLoading(true);
    fetchAccessLogs(mode, selection, startDate, endDate)
      .then(setItems)
      .catch((e) => {
        console.log(e);
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [mode, selection, startDate, endDate]);

  React.useEffect(() => {
    load();
  }, []);

  return (
    <div className="admin-access-logs">
      <div className="row">
        <label className="col-5">{subject}</label>
        <div className="col-5 d-flex">
          <Form.Control
            type="date"
            size="sm"
            aria-label="start-date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <span className="px-2">{getLocalization('to')}</span>
          <Form.Control
            type="date"
            size="sm"
            aria-label="end-date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="col-2">
          <Button size="sm" onClick={load}>
            {getLocalization('showlogs')}
          </Button>
        </div>
      </div>

      {loading && <ProgressBar striped now={100} animated />}

      {!loading && items !== null && items.length === 0 && <p>{getLocalization('noLogs')}</p>}

      {!loading && items !== null && items.length > 0 && (
        <Table striped bordered hover size="sm" responsive>
          <thead>
            <tr>
              <th>{getLocalization('date')}</th>
              <th>{getLocalization('form')}</th>
              <th>{getLocalization('action')}</th>
              <th>{getLocalization('user')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>{formatDate(item.date)}</td>
                <td>{item.questionnaireName}</td>
                <td>{item.description}</td>
                <td>{item.userName}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
